/**
 * PostHog plugin — product analytics across every ClaimShield front end.
 *
 * This file is deliberately the ONLY place PostHog is configured. Six apps
 * share this package, and a masking rule written once is a masking rule that
 * cannot be forgotten in the sixth app. Do not call `posthog.init()` anywhere
 * else, and do not loosen anything here without reading §4.4 and §7.2 of
 * knowledge/product-analytics/TRACK.md — the constants below are rulings, not
 * defaults someone picked.
 *
 * Three properties this install must keep:
 *
 *   1. It is same-origin. `api_host` is the RELATIVE path '/ingest'. Who
 *      answers that path depends on where the app is hosted, and BOTH are
 *      first-party:
 *        - VPS apps (portal, apply, demo, vantage) — a Caddy `handle_path
 *          /ingest/*` block in infrastructure/caddy-config/Caddyfile.
 *        - Cloudflare Pages apps (broker, driver, mechanic, legacy rewards) —
 *          a Pages Function at
 *          `functions/ingest/[[path]].ts` in that package. There is no Caddy in
 *          front of a Pages project, so the Caddy stanza does not reach them.
 *      Either way the browser only ever talks to our own origin, which is what
 *      lets every app keep `connect-src 'self'` — no CSP is widened by this
 *      project (Ahmed, 2026-09-10, decision 2). If you ever find yourself
 *      writing an absolute posthog.com URL here, the decision has changed and
 *      that is a conversation, not an edit.
 *
 *   2. It captures no page content. Autocapture, dead clicks, rage clicks and
 *      exception capture are all OFF, and both mask flags are ON. This app
 *      family renders driver PII — `SsnReveal.vue` in this very package
 *      fetches a full SSN on click — so "PostHog reads the DOM" is not an
 *      acceptable default here.
 *
 *   3. It runs only where it has been switched on, by exact hostname.
 *      ENABLED_HOSTS below is an ALLOWLIST. A new surface sends nothing until
 *      its host is added here on purpose.
 */
import type { App, Plugin } from 'vue'
import type { RouteLocationNormalized, Router } from 'vue-router'
// TYPE-ONLY. A value import here would put ~283 KB of vendor SDK into the main
// chunk of every app that reaches this module — see the `loadPosthog` comment.
// Type imports are erased at build time and cost the bundle nothing.
import type { PostHog } from 'posthog-js'

/**
 * The path the SDK is pointed at — relative, see property 1 above. The proxy in
 * front of it (Caddy on the VPS, a Pages Function on Cloudflare) strips this
 * prefix and forwards to the regional PostHog hosts. It is deliberately NOT
 * '/analytics', '/tracking', '/telemetry', '/posthog' or '/ph': PostHog's own
 * proxy guide warns that blockers match those names, which would defeat the
 * whole point of proxying.
 *
 * IMPORTED, not redeclared. The sender and the Pages Function that answers it
 * must agree on this string exactly, and two constants that must match are a
 * drift waiting to happen — the Caddy block is already a third copy we cannot
 * import. `posthog-proxy.ts` has no imports of its own, so this direction costs
 * the browser bundle a string and nothing else; the reverse would drag
 * posthog-js into a Cloudflare Worker.
 */
import { INGEST_PREFIX as INGEST_PATH } from './posthog-proxy'

/** Apps that may install this plugin. `rewards` is a legacy input; driver hosts emit `driver`. */
export type PosthogApp = 'portal' | 'ops' | 'apply' | 'driver' | 'rewards' | 'mechanic' | 'broker' | 'vantage' | 'demo'

/**
 * Public, write-only project token. Safe to ship in-page — it is the same class
 * of value as the one already live in the marketing site's Analytics.astro, and
 * it cannot read anything back. Overridable at build time for a second project.
 */
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY ?? 'phc_wT8umAxwTh5KSSUZ7R9y7j8vryZyY97T57mFmk6A9Wdq'

/**
 * The real PostHog app URL. Required alongside a proxied `api_host` or the
 * toolbar and replay links point at our own domain and break. It is used to
 * build links, never to send data, so it needs no CSP entry.
 */
const UI_HOST = 'https://us.posthog.com'

/**
 * Pinned, not 'latest'. PostHog ships behaviour changes behind dated default
 * bundles; pinning means an SDK upgrade cannot silently move our behaviour.
 * Every security-relevant option is ALSO set explicitly below, so bumping this
 * date can never turn capture back on by itself.
 */
const POSTHOG_DEFAULTS = '2026-08-30'

/**
 * Allowlist. An app sends nothing until its exact production hostname appears
 * here.
 *
 * Broker, driver, mechanic and legacy rewards reach PostHog through a
 * same-origin `/ingest/*` Pages Function rather than Caddy. Driver and mechanic
 * render driver data, so URL token redaction and disabled page-content capture
 * are required on all three driver app hosts.
 *
 * Ops joined on 2026-09-11. It renders driver PII, which is exactly why the
 * plugin's masking settings are not optional and why replay is off everywhere:
 * autocapture, dead clicks, rage clicks and exception capture are all disabled
 * and both mask flags are on, so nothing reads the DOM. Ops, being Cloudflare
 * Pages, reaches PostHog through its own `functions/ingest/[[path]].ts`.
 *
 * WARNING `portal.claimshieldpro.com` joined on 2026-09-11 and is HELD OUT again
 * as of the same day (Ahmed). This is a TEMPORARY hold, not a ruling against
 * portal - put it back as part of this project's own deploy, and only then.
 *
 * Why it was pulled. An unrelated fix (the carrier audit-pass page) needed
 * `frontend-portal` rebuilt, and the portal image had not been rebuilt since
 * before this plugin landed. That deploy would therefore have switched carrier
 * analytics on as a SIDE EFFECT, ahead of Ahmed's implement-all-then-deploy-once
 * instruction (TRACK 7.7) - the same failure the demo removal below exists to
 * prevent, one surface over. It would also have shipped BROKEN: portal's
 * `import posthog_ingest` is in the repo's Caddyfile but is not on the live VPS,
 * and a frontend-only deploy does not reload Caddy, so every event would have
 * POSTed into a 404 on our own origin.
 *
 * To put portal back, both halves have to ship together: this entry, and a Caddy
 * deploy carrying the `import posthog_ingest` line in the portal site block.
 *
 * ⚠ `demo.claimshieldpro.com` WAS here and was REMOVED (Ahmed, 2026-09-11:
 * "leave the demo alone from this"). Do not put it back without asking him.
 * The demo is Prosser's prospect-facing sales surface and is deliberately
 * frozen on a pinned build; leaving it on this list would mean the next person
 * to re-pin it silently turns analytics on in front of a prospect, which is the
 * exact thing that ruling prevents. The Caddy `/ingest` route on that host is
 * still live and is now unused — harmless, and removing it is a VPS deploy
 * nobody has asked for.
 */
export const ENABLED_HOSTS: readonly string[] = [
  'network.claimshieldpro.com',
  'rewards.claimshieldpro.com',
  'driver.claimshieldpro.com',
  'mechanic.claimshieldpro.com',
  'ops.claimshieldpro.com',
]

/**
 * Pure so it can be tested without a browser. Exact match only — a `endsWith`
 * or a regex here would admit `demo.claimshieldpro.com.evil.tld`, and the whole
 * safety property of this file is that the production portal ships this code
 * and stays silent.
 */
export function posthogEnabledForHost(hostname: string | null | undefined): boolean {
  if (!hostname) return false
  return ENABLED_HOSTS.includes(hostname)
}

/** Host, not route or package history, determines the driver app surface. */
export function posthogAppForHost(hostname: string, fallback: PosthogApp): PosthogApp {
  if (hostname === 'mechanic.claimshieldpro.com') return 'mechanic'
  if (hostname === 'driver.claimshieldpro.com' || hostname === 'rewards.claimshieldpro.com') {
    return 'driver'
  }
  return fallback
}

/** True only on an allowlisted production host. Dev servers and previews never send. */
function isEnabledHere(): boolean {
  if (typeof window === 'undefined') return false
  return posthogEnabledForHost(window.location.hostname)
}

/**
 * The live SDK handle, or null until it has loaded AND initialised.
 *
 * Everything below checks this rather than a boolean, so there is exactly one
 * source of truth for "is PostHog usable right now" and no window in which a
 * flag says yes while the module is still in flight.
 */
let ph: PostHog | null = null

/** Guards against a second `app.use()` racing the first one's dynamic import. */
let starting = false

/** The most recent route name, held until the SDK is ready to receive it. */
let pendingRouteName: string | null = null

/** Has PostHog actually loaded and initialised in this page? Used by tests and callers. */
export function posthogActive(): boolean {
  return ph !== null
}

/**
 * Property values are ids and enums, never user content.
 *
 * Same rule the in-house tracker already enforces server-side, restated here
 * because this endpoint is a vendor's and nothing on their side will refuse a
 * bad value. Anything that is not a scalar, or a string longer than a uuid plus
 * headroom, is dropped rather than truncated — a silently shortened value is
 * the failure mode that looks like success.
 */
const MAX_PROPERTY_CHARS = 200
const MAX_PROPERTIES = 10

export function safeProperties(input?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!input) return undefined
  const out: Record<string, unknown> = {}
  let n = 0
  for (const [key, value] of Object.entries(input)) {
    if (n >= MAX_PROPERTIES) break
    if (value === null || typeof value === 'number' || typeof value === 'boolean') {
      out[key] = value
      n++
    } else if (typeof value === 'string' && value.length <= MAX_PROPERTY_CHARS) {
      out[key] = value
      n++
    }
    // Objects, arrays and over-long strings are dropped on purpose.
  }
  return n > 0 ? out : undefined
}

/** Registered route tags persist in the SDK, so clear a prior rewards section. */
export function posthogRegistration(
  hostname: string,
  requestedApp: PosthogApp,
  properties?: Record<string, unknown>,
): Record<string, unknown> {
  const app = posthogAppForHost(hostname, requestedApp)
  return {
    ...(app === 'driver' || app === 'mechanic' ? { section: null } : {}),
    ...safeProperties(properties),
    app,
  }
}

/**
 * Capture a semantic event. Properties are filtered by `safeProperties`.
 *
 * A call made before the SDK finishes loading is DROPPED, not queued. That is
 * the same behaviour as before this module went lazy (it dropped anything sent
 * before `init` returned), and it is the honest choice: a queue would hold
 * caller-supplied values in memory for an unbounded time on a page that may
 * render driver data, to save an event nobody is reading yet. If a future
 * caller genuinely needs the first 200ms of a session, buffer at that call
 * site where the retention question can be answered concretely.
 */
export function capture(event: string, properties?: Record<string, unknown>): void {
  if (!ph) return
  ph.capture(event, safeProperties(properties))
}

/**
 * Associate the session with a user and their carrier.
 *
 * Ids only — never an email address, a name, an SSN or a date of birth. The
 * carrier group is what makes every question answerable per client, which is
 * the whole reason this project exists at all.
 */
export function identify(userId: string, clientId?: string | null): void {
  if (!ph || !userId) return
  ph.identify(userId)
  if (clientId) ph.group('client', clientId)
}

/** Who the analytics session belongs to. Ids and one flag — nothing else. */
export interface AnalyticsIdentity {
  /**
   * The REAL signed-in user's id. Never an impersonated one: see
   * `impersonating` below.
   */
  userId: string
  /** The real user's own carrier id, when they are linked to one. */
  clientId?: string | null
  /** True while an admin is using View As — viewing as another user or carrier. */
  impersonating?: boolean
}

/**
 * Point the analytics session at a person, or clear it. The single entry point
 * the auth store calls; `identify` above is the primitive underneath.
 *
 * **Impersonation is the case worth reading.** An admin using View As browses a
 * carrier's portal with that carrier's data on screen. If those page views were
 * grouped to the carrier, the admin's afternoon of support work would read as
 * that carrier being active — which corrupts the exact question this project
 * exists to answer ("which carriers adopted the portal, which went quiet"). It
 * is a quiet, plausible-looking corruption: the numbers would go UP, and the
 * carriers most likely to be inflated are the ones someone is investigating,
 * i.e. the struggling ones.
 *
 * So while impersonating we identify the REAL admin, attach NO carrier group,
 * and stamp `impersonating: true` on every subsequent event so those sessions
 * can be excluded from any view. Nothing is hidden; it is just not attributed
 * to someone who did not do it.
 */
/**
 * What `syncIdentity` should do for a given identity. Pure, so the rule can be
 * tested exhaustively without a browser or a loaded SDK — the same reason
 * `resolvePosthogUpstream` is split out in posthog-proxy.ts.
 *
 * `group: null` is not "no opinion", it is "deliberately do not group". The
 * impersonation case depends on that distinction.
 */
export type IdentityAction =
  | { kind: 'reset' }
  | { kind: 'identify'; userId: string; impersonating: boolean; group: string | null }

export function resolveIdentityAction(identity: AnalyticsIdentity | null): IdentityAction {
  if (!identity || !identity.userId) return { kind: 'reset' }

  const impersonating = identity.impersonating === true
  return {
    kind: 'identify',
    userId: identity.userId,
    impersonating,
    // Withheld while impersonating — see the syncIdentity doc comment.
    group: impersonating ? null : (identity.clientId ?? null),
  }
}

export function syncIdentity(identity: AnalyticsIdentity | null): void {
  if (!ph) return
  const action = resolveIdentityAction(identity)

  if (action.kind === 'reset') {
    ph.reset()
    return
  }

  ph.identify(action.userId)
  ph.register({ impersonating: action.impersonating })
  if (action.group) ph.group('client', action.group)
}


/** Clear identity on logout so the next user on this browser is a new person. */
export function resetIdentity(): void {
  if (!ph) return
  ph.reset()
}

/** One rewrite applied to every string an event carries. */
export interface RedactRule {
  pattern: RegExp
  replacement: string
}

/** One-time driver and garage credentials, including URLs embedded in query strings. */
export const DRIVER_TOKEN_REDACT_RULES: RedactRule[] = [
  { pattern: /\/auth\/verify\/[^/?#\s]+/g, replacement: '/auth/verify/:token' },
  { pattern: /\/garage\/annual\/[^/?#\s]+/g, replacement: '/garage/annual/:token' },
  { pattern: /%2[fF]auth%2[fF]verify%2[fF][^&#?\s]+/g, replacement: '%2Fauth%2Fverify%2F:token' },
  { pattern: /%2[fF]garage%2[fF]annual%2[fF][^&#?\s]+/g, replacement: '%2Fgarage%2Fannual%2F:token' },
]

/**
 * Pure. Rewrites every string, at any depth, through each rule.
 *
 * Some routes carry a bearer token in the URL — the broker app's
 * `/verify/<token>` IS the credential to a dispatch record — and the SDK copies
 * the URL into several properties (`$current_url`, `$pathname`, `$referrer`,
 * `$initial_*` under `$set_once`). A rule applied to one key leaks through the
 * others, so this walks them all. DOM masking does not touch URLs.
 */
export function redactEventProperties(
  props: Record<string, unknown>,
  rules: RedactRule[],
): Record<string, unknown> {
  if (!rules.length) return props
  const walk = (v: unknown): unknown => {
    if (typeof v === 'string') {
      return rules.reduce(
        (s, r) => s.replace(new RegExp(r.pattern.source, 'g'), r.replacement),
        v,
      )
    }
    if (Array.isArray(v)) return v.map(walk)
    if (v && typeof v === 'object') {
      // Keys too: some payloads index by URL (`$heatmap_data` is keyed by
      // location.href), so a value-only walk leaves the token in the key.
      return Object.fromEntries(
        Object.entries(v as Record<string, unknown>).map(([k, x]) => [walk(k) as string, walk(x)]),
      )
    }
    return v
  }
  return walk(props) as Record<string, unknown>
}

export interface PosthogPluginOptions {
  /** Which app is installing. Stamped on every event as `app`. */
  app: PosthogApp
  /** Optional route-specific tag for a SPA that serves more than one product. */
  appForRoute?: (route: RouteLocationNormalized) => PosthogApp
  /** Safe route tags such as `section: 'rewards'`; cleared on the next route. */
  propertiesForRoute?: (route: RouteLocationNormalized) => Record<string, unknown>
  /** Vue Router, when the app has one. Without it, only the initial page view is captured. */
  router?: Router
  /** URL fragments that must never leave the browser (tokens in paths). */
  redact?: RedactRule[]
}

/**
 * Load the SDK, then initialise it. Called only on an allowlisted host.
 *
 * WHY THIS IS A DYNAMIC IMPORT (Ahmed, 2026-09-11). `posthog-js` is ~283 KB.
 * A static import put it in the main chunk of every app that reaches this
 * module — and because `src/index.ts` re-exports this plugin, "reaches this
 * module" meant every app that imports the shared barrel at all: portal, ops,
 * broker, and **apply**, the driver form that collects SSNs and dates of birth,
 * which ships the SDK today and never uses it. Measured on rewards, the one app
 * with no barrel imports: 496,645 -> 780,010 bytes of JS, +57%, on a
 * mobile driver-facing app.
 *
 * As a dynamic import the SDK becomes its own chunk that is FETCHED ONLY when
 * this function runs, which is only on an allowlisted host. Apps that carry the
 * code but are not switched on now download nothing.
 *
 * Kept off the critical path deliberately: the page renders, then analytics
 * arrives. Analytics is never worth delaying a driver's login screen.
 */
async function loadPosthog(options: PosthogPluginOptions): Promise<void> {
  const posthog = (await import('posthog-js')).default

  posthog.init(POSTHOG_KEY, {
    api_host: INGEST_PATH,
    ui_host: UI_HOST,
    defaults: POSTHOG_DEFAULTS,

    // --- Nothing reads the page. See property 2 in the file header. ---
    autocapture: false,
    capture_dead_clicks: false,
    rageclick: false,
    capture_exceptions: false,
    // Off in code, not left to the project's remote setting: heatmap batches
    // are keyed by the raw page URL, tokens included, and reach their own table.
    capture_heatmaps: false,
    mask_all_text: true,
    mask_all_element_attributes: true,

    // --- Replay is off everywhere (Ahmed, 2026-09-10, decision 3). ---
    // Turning this on anywhere is a new decision, not a config tweak: replay
    // records the screen, and an ops session records a consultant revealing
    // a full SSN outside the audit_logs trail that governs every other
    // reveal of that number.
    disable_session_recording: true,

    // SPA page views. Without 'history_change' only the first load is seen,
    // because these apps navigate through the History API and never reload.
    capture_pageview: 'history_change',

    // No person profile until someone is identified.
    person_profiles: 'identified_only',

    // Tokens in URLs never leave the browser. Runs on every event, page views
    // included, before it is queued.
    before_send: (event) => {
      if (!event) return event
      const rules = [...DRIVER_TOKEN_REDACT_RULES, ...(options.redact ?? [])]
      event.properties = redactEventProperties(event.properties ?? {}, rules)
      if (event.$set) event.$set = redactEventProperties(event.$set, rules)
      if (event.$set_once) event.$set_once = redactEventProperties(event.$set_once, rules)
      return event
    },
  })

  // Published only after init returns, so nothing can reach a half-built SDK.
  ph = posthog
  const route = options.router?.currentRoute.value
  posthog.register(posthogRegistration(
    window.location.hostname,
    route && options.appForRoute ? options.appForRoute(route) : options.app,
    route && options.propertiesForRoute ? options.propertiesForRoute(route) : undefined,
  ))
  if (pendingRouteName !== null) posthog.register({ route_name: pendingRouteName })
}

export const posthogPlugin: Plugin = {
  install(_vueApp: App, options: PosthogPluginOptions) {
    if (starting || ph || !isEnabledHere()) return
    starting = true

    // The router hook is attached SYNCHRONOUSLY, before the SDK exists, so no
    // navigation is missed while the chunk is in flight. Until `ph` is set the
    // latest route name is simply held.
    if (options.router) {
      if (options.appForRoute || options.propertiesForRoute) {
        options.router.beforeEach((to) => {
          if (ph) ph.register(posthogRegistration(
            window.location.hostname,
            options.appForRoute?.(to) ?? options.app,
            options.propertiesForRoute?.(to),
          ))
        })
      }
      options.router.afterEach((to) => {
        const name = typeof to.name === 'string' ? to.name : null
        pendingRouteName = name
        // `capture_pageview: 'history_change'` already follows the History API,
        // so this exists to stamp the ROUTE NAME — a stable identifier we
        // author — rather than to re-capture the view. Route names are literals
        // in our own source; URLs can carry ids.
        if (ph) ph.register({
          route_name: name,
          ...posthogRegistration(
            window.location.hostname,
            options.appForRoute?.(to) ?? options.app,
            options.propertiesForRoute?.(to),
          ),
        })
      })
    }

    // Deliberately not awaited: `install` is synchronous in Vue's plugin
    // contract, and a failed analytics load must never take the app down with
    // it. A rejected import leaves `ph` null and every helper above a no-op,
    // which is the same state as an app that was never switched on.
    void loadPosthog(options).catch(() => {
      starting = false
    })
  },
}
